import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "../App";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles } from "lucide-react";
import { Button } from "./ui/button";

const labels = {
  fr: { title: "Comparaison GED personnalisée", text: "Notre analyseur IA vous recommande les 3 meilleures solutions en 5 minutes.", cta: "Démarrer l'analyse IA", dismiss: "Non merci" },
  es: { title: "Comparativa GED personalizada", text: "Nuestro analizador IA le recomienda las 3 mejores soluciones en 5 minutos.", cta: "Iniciar análisis IA", dismiss: "No, gracias" },
  en: { title: "Personalized DMS comparison", text: "Our AI analyzer recommends the top 3 solutions for you in 5 minutes.", cta: "Start AI analysis", dismiss: "No thanks" }
};

export const ReturnVisitorPopup = ({ onStartAnalysis }) => {
  const { language } = useLanguage();
  const [showModal, setShowModal] = useState(false);
  const [showMobileBar, setShowMobileBar] = useState(false);
  const lbl = labels[language] || labels.fr;

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  // Desktop: exit intent (mouse leaves viewport top)
  useEffect(() => {
    if (isMobile) return;
    const alreadyShown = sessionStorage.getItem("popup_shown");
    const isReturn = localStorage.getItem("documatch_has_visited");
    if (!isReturn || alreadyShown) {
      if (!isReturn) localStorage.setItem("documatch_has_visited", "true");
      return;
    }

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !sessionStorage.getItem("popup_shown")) {
        setShowModal(true);
        sessionStorage.setItem("popup_shown", "true");
      }
    };

    // Also show after 45s as fallback
    const timer = setTimeout(() => {
      if (!sessionStorage.getItem("popup_shown")) {
        setShowModal(true);
        sessionStorage.setItem("popup_shown", "true");
      }
    }, 45000);

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => { document.removeEventListener("mouseleave", handleMouseLeave); clearTimeout(timer); };
  }, [isMobile]);

  // Mobile: sticky bar after 30s of scrolling
  useEffect(() => {
    if (!isMobile) return;
    const alreadyShown = sessionStorage.getItem("popup_shown");
    const isReturn = localStorage.getItem("documatch_has_visited");
    if (!isReturn) { localStorage.setItem("documatch_has_visited", "true"); return; }
    if (alreadyShown) return;

    const timer = setTimeout(() => {
      setShowMobileBar(true);
      sessionStorage.setItem("popup_shown", "true");
    }, 30000);

    return () => clearTimeout(timer);
  }, [isMobile]);

  const handleCTA = () => {
    setShowModal(false);
    setShowMobileBar(false);
    onStartAnalysis();
  };

  // Desktop modal (exit intent)
  if (showModal && !isMobile) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
          onClick={() => setShowModal(false)}
          role="dialog" aria-modal="true" aria-label={lbl.title}
          data-testid="exit-intent-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
            onClick={e => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative"
          >
            <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600" aria-label="Close">
              <X size={20} />
            </button>
            <div className="text-center">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles size={24} className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">{lbl.title}</h3>
              <p className="text-sm text-slate-600 mb-6">{lbl.text}</p>
              <Button onClick={handleCTA} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 mb-3" data-testid="popup-cta">
                <Sparkles size={16} className="mr-2" />{lbl.cta}
              </Button>
              <button onClick={() => setShowModal(false)} className="text-sm text-slate-400 hover:text-slate-600">{lbl.dismiss}</button>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Mobile sticky bar (max 30% screen height)
  if (showMobileBar && isMobile) {
    return (
      <motion.div
        initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="fixed bottom-0 left-0 right-0 z-[90] bg-white border-t border-slate-200 shadow-2xl p-4 max-h-[30vh]"
        role="complementary" aria-label={lbl.title}
        data-testid="mobile-sticky-bar"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{lbl.title}</p>
            <p className="text-xs text-slate-500 truncate">{lbl.text}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button size="sm" onClick={handleCTA} className="bg-blue-600 text-white text-xs px-3">{lbl.cta}</Button>
            <button onClick={() => setShowMobileBar(false)} className="text-slate-400" aria-label="Close"><X size={16} /></button>
          </div>
        </div>
      </motion.div>
    );
  }

  return null;
};

export default ReturnVisitorPopup;
